/**
 * CustomWorkflow: extensible interactive-screen driver used by the
 * "kind: custom" scenarios.
 *
 * Each scenario provides:
 *   steps: [{
 *     screen: string,           // one of SCREENS
 *     sidebarActive: string,    // DesktopShell activeApp value
 *     title?: string,           // small caption above the screen
 *     hint?: string,            // promoter hint shown outside manager view
 *     data: object,             // screen-specific payload
 *     actionLabel?: string,     // CTA button label (advance to next step)
 *     nextLabel?: string,       // alternative "Next" label
 *     terminal?: boolean,       // triggers the scenario summary card
 *     outcome?: string,         // key into wf.summaries
 *   }]
 *
 * Everything below renders inside the existing DesktopShell so nav + business
 * chrome stay identical to the rest of the demo suite.  No fetches, no writes.
 */

import { useEffect, useMemo, useState } from "react";
import {
  AlertTriangle, CheckCircle2, ChevronRight, ChevronLeft, Bell, Search,
  Send, SkipForward, RefreshCw, Eye, XCircle, MessageSquare, ArrowRightLeft,
  Plus, Users, Car, ClipboardCheck, ClipboardList, Star, Camera,
  Wrench, Clock, PlayCircle, PauseCircle, Coffee, LogOut, KeyRound,
  Activity as ActivityIcon, Edit, PowerOff, Power, X, History,
  User, ArrowRight, ArrowLeft, Circle, ExternalLink,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { DemoBadge } from "../DemoPanels";
import DesktopShell from "./DesktopShell";
import ScenarioSummary from "./ScenarioSummary";
import PickupHandoverWizard from "../../../../components/pickup-handover/PickupHandoverWizard";
import InspectionSheet from "../../../../components/InspectionSheet";
import ReturnReadinessPanel from "../../../../components/ReturnReadinessPanel";
import WarningCard from "../../../../components/warnings/WarningCard";
import ReassignVehicleModal from "../../../../components/ReassignVehicleModal";
import CustomerConfirmation from "../../../CustomerConfirmation";
import CustomerBookingDetail from "../../../CustomerBookingDetail";
import { VehicleOverviewTab } from "../../../../components/vehicles/VehicleOverviewTab";
import { VehicleDamageMap } from "../../../../components/VehicleDamageMap";
import ChatSimulation from "../../../../components/walkthrough2/ChatSimulation";
import { INCIDENT_SCENARIO } from "../../../../components/walkthrough2/incidentScenario";
import { INCIDENT_TYPE_META, SEVERITY_META, SeverityBadge } from "../../../../lib/incidents";

// -----------------------------------------------------------------
// Public driver
// -----------------------------------------------------------------
export default function CustomWorkflow({
  scenario, managerView, resetToken, deviceMode = "desktop",
  audience = "promoter", embedded = false, onComplete,
}) {
  const wf = scenario.workflow;
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [showSummary, setShowSummary] = useState(false);
  const [branchOutcome, setBranchOutcome] = useState(null);

  useEffect(() => {
    setStep(0);
    setShowSummary(false);
    setBranchOutcome(null);
  }, [resetToken, wf]);

  // Homepage embeds show only up to `wf.embedStopAtScreen` when the
  // scenario defines one (currently just the vehicle-breakdown incident:
  // stop at "the business sees the replacement confirmed", per the public
  // homepage presentation split). The standalone /demo-business/scenarios
  // page (embedded=false) always gets the full array, unchanged: this is a
  // slice of the SAME single steps array, never a second copy of it.
  const allSteps = wf.steps || [];
  let steps = allSteps;
  if (embedded && wf.embedStopAtScreen) {
    const capIdx = allSteps.findIndex((s) => s.screen === wf.embedStopAtScreen);
    if (capIdx !== -1) steps = allSteps.slice(0, capIdx + 1);
  }
  const current = steps[step];
  const isLast = step >= steps.length - 1;

  useEffect(() => {
    if (!embedded || !current?.guidance) return;
    window.parent.postMessage({
      type: "CONFIRMPH_DEMO_SUBSTEP",
      scenarioId: scenario.id,
      heading: current.title,
      instruction: current.guidance[0],
      context: current.guidance[1],
    }, window.location.origin);
  }, [embedded, scenario.id, current?.title, current?.guidance]);

  const advance = (result) => {
    // A secondary branch can finish truthfully without falling back into the
    // remaining successful-path steps. The namespaced payload avoids
    // confusing production-component callback values with demo navigation.
    if (result?.demoOutcome) {
      setBranchOutcome(result.demoOutcome);
      setShowSummary(true);
      onComplete?.();
      return;
    }
    if (isLast) {
      if (current?.terminal !== false) setShowSummary(true);
      onComplete?.();
    } else {
      setStep((s) => s + 1);
    }
  };
  const back = () => setStep((s) => Math.max(0, s - 1));

  const outcomeKey = branchOutcome || current?.outcome || steps[steps.length - 1]?.outcome;
  const summary = outcomeKey && wf.summaries ? wf.summaries[outcomeKey] : null;

  useEffect(() => {
    if (!embedded || !isLast || !current?.outcome) return undefined;
    if (!["banner-info", "review-public", "activity-timeline", "employee-performance", "vehicles-list"].includes(current.screen)) return undefined;
    const timer = setTimeout(() => onComplete?.(), 1800);
    return () => clearTimeout(timer);
  }, [embedded, isLast, current?.outcome, current?.screen, onComplete]);

  const content = current ? (
    <div className="space-y-3">
      {current.title && (
        <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-emerald-800">
          {current.title}
        </div>
      )}
      <ScreenSwitch screen={current.screen} data={current.data || {}} onAction={advance} isLast={isLast} />
    </div>
  ) : null;

  const shell = (
    <DesktopShell business={wf.business}
                   activeApp={current?.sidebarActive || "dashboard"}
                   onSwitchApp={() => { /* nav locked inside scenario */ }}>
      {content}
      {/* Generic demo pagination is not product UI, so homepage embeds never
          expose it. The actual control inside each screen drives the flow. */}
      <div className={embedded ? "hidden" : "mt-4 flex flex-wrap items-center justify-between gap-2"}>
        <div className={embedded ? "hidden" : "text-[11px] text-slate-500"}>
          Step {step + 1} / {steps.length}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={back} disabled={step === 0}
                  data-testid="custom-step-back"
                  className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full border border-slate-300 bg-white text-slate-700 disabled:opacity-40">
            <ChevronLeft className="w-3.5 h-3.5" /> Back
          </button>
          {!(embedded && showSummary) && (
            <button onClick={advance}
                    data-testid="custom-step-next"
                    className="inline-flex items-center gap-1 text-xs px-4 py-1.5 rounded-full bg-[#1B4332] text-white font-medium">
              {current?.actionLabel || current?.nextLabel || (isLast ? "Finish" : "Next")}
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>
      {audience === "promoter" && !managerView && !embedded && current?.hint && (
        <div className="mt-3 rounded-lg bg-slate-50 border border-slate-200 p-3 text-xs text-slate-700">
          <div className="uppercase tracking-wider text-[10px] font-semibold text-slate-500">
            Promoter hint
          </div>
          <div className="mt-0.5">{current.hint}</div>
        </div>
      )}
    </DesktopShell>
  );

  const _dm = deviceMode; void _dm; // deviceMode acknowledged; we render desktop-only here
  return (
    <>
      {shell}
      {showSummary && summary && !embedded && (
        <ScenarioSummary summary={summary} audience={audience}
                          onReset={() => { setStep(0); setShowSummary(false); setBranchOutcome(null); }}
                          onChooseAnother={() =>
                            navigate(audience === "public"
                                       ? "/demo-business/scenarios"
                                       : "/promoter/demos")} />
      )}
    </>
  );
}

// -----------------------------------------------------------------
// Screen switch
// -----------------------------------------------------------------
function ScreenSwitch({ screen, data, onAction, isLast }) {
  const map = {
    "bookings-list":         BookingsListScreen,
    "booking-detail":        BookingDetailScreen,
    "edit-booking-dates":    EditBookingDatesScreen,
    "find-booking":          FindBookingScreen,
    "notepad-recover":       NotepadRecoverScreen,
    "reminders-list":        RemindersListScreen,
    "warnings-list":         WarningsListScreen,
    "activity-timeline":     ActivityTimelineScreen,
    "employees-list":        EmployeesListScreen,
    "employee-form":         EmployeeFormScreen,
    "employee-performance":  EmployeePerformanceScreen,
    "staff-availability":    StaffAvailabilityScreen,
    "vehicles-list":         VehiclesListScreen,
    "vehicle-form":          VehicleFormScreen,
    "vehicle-detail":        VehicleDetailScreen,
    "maintenance-form":      MaintenanceFormScreen,
    "damage-incident":       DamageIncidentScreen,
    "checklists-today":      ChecklistsTodayScreen,
    "checklist-run":         ChecklistRunScreen,
    "review-invite":         ReviewInviteScreen,
    "review-public":         PublicReviewScreen,
    "confirmation-preview":  ConfirmationPreviewScreen,
    "banner-info":           BannerInfoScreen,
    "pickup-handover-live":  PickupHandoverLiveScreen,
    "return-readiness-live": ReturnReadinessLiveScreen,
    "return-inspection-live": ReturnInspectionLiveScreen,
    "incident-chat-live": IncidentChatLiveScreen,
    "vehicle-command-live": VehicleCommandLiveScreen,
    "damage-record-live": DamageRecordLiveScreen,
    "incident-log-live": IncidentLogLiveScreen,
    "warning-live": WarningLiveScreen,
    "warning-reassign-live": WarningReassignLiveScreen,
    "reconfirmation-live": ReconfirmationLiveScreen,
    "customer-confirmation-live": CustomerConfirmationLiveScreen,
    "business-resolution-live": BusinessResolutionLiveScreen,
  };
  const Comp = map[screen];
  if (!Comp) {
    return <div className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-xs text-rose-700">
      Missing screen: {screen}
    </div>;
  }
  return <Comp data={data} onAction={onAction} isLast={isLast} />;
}

// These three screens mount the same production components used by
// BookingDetail.  `preloaded` switches those components to their existing
// local/demo adapter, so every button and field is real UI while no demo can
// write to the API.
function PickupHandoverLiveScreen({ data, onAction }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="min-h-[520px]" data-testid="screen-pickup-handover-live">
      <PickupHandoverWizard
        bookingId="demo-pickup-booking"
        open={open}
        preloaded={data.handover}
        onClose={() => setOpen(false)}
        onDone={onAction}
      />
      {!open && <button type="button" onClick={() => setOpen(true)} className="w-full rounded-lg bg-[#1B4332] py-2.5 text-sm font-semibold text-white">Reopen pickup handover</button>}
    </div>
  );
}

function ReturnReadinessLiveScreen({ data, onAction }) {
  return (
    <div className="max-w-3xl mx-auto py-4" data-testid="screen-return-readiness-live">
      <div className="mb-3 rounded-xl border border-slate-200 bg-white p-4">
        <div className="flex items-start justify-between gap-2">
          <div><h2 className="font-display text-lg font-semibold">Maria Santos</h2><p className="text-xs font-mono text-slate-500">Booking DEMO-RET-2211</p></div>
          <StatusPill tone={pillToneForStatus(data.booking.status)}>{data.booking.status}</StatusPill>
        </div>
        <div className="mt-2 text-xs text-slate-600">Toyota Vios · WSJ-2211 · Aug 18, 09:00 → Aug 20, 09:00</div>
      </div>
      <ReturnReadinessPanel
        booking={data.booking}
        returnInspection={data.returnInspection}
        nextBooking={data.nextBooking}
        onRecordReturn={onAction}
        onOpenInspection={onAction}
        onOpenNextBooking={onAction}
        compact
      />
    </div>
  );
}

function ReturnInspectionLiveScreen({ data, onAction }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="min-h-[520px]" data-testid="screen-return-inspection-live">
      <InspectionSheet
        bookingId="demo-return-booking"
        type="return"
        open={open}
        preloaded={data}
        onClose={() => setOpen(false)}
        onDone={(inspection) => { if (inspection?.status === "finalized") onAction(); }}
      />
      {!open && <button type="button" onClick={() => setOpen(true)} className="w-full rounded-lg bg-[#1B4332] py-2.5 text-sm font-semibold text-white">Reopen return inspection</button>}
    </div>
  );
}

const INCIDENT_BOOKING = {
  id: INCIDENT_SCENARIO.booking.id, booking_reference: INCIDENT_SCENARIO.booking.reference,
  customer_name: INCIDENT_SCENARIO.affectedRenter.name, customer_email: INCIDENT_SCENARIO.affectedRenter.email,
  customer_phone: INCIDENT_SCENARIO.affectedRenter.phone, status: "awaiting_customer_confirmation",
  start_datetime: "2026-09-04T09:00:00+08:00", end_datetime: "2026-09-06T18:00:00+08:00",
  price: 6400, deposit_required: 2000, pickup_location: "Traveler Car Rental office",
  return_location: "Traveler Car Rental office", reconfirmation_required: true,
};
const INCIDENT_WARNING = {
  id: "incident-warning-vb-1", warning_type: "vehicle_unavailable", severity: "critical", status: "active",
  title: "Upcoming confirmed booking affected",
  message: "Kia Rio · PYR-5502 is unavailable and assigned to TCR-4821 for Anna Reyes tomorrow at 09:00.",
  recommended_action: "Notify the renter and reassign a suitable available vehicle, or cancel/change the booking.",
  related_booking_id: INCIDENT_SCENARIO.booking.id, related_vehicle_id: INCIDENT_SCENARIO.damagedVehicle.id,
  detected_at: "2026-09-03T09:15:00+08:00", last_detected_at: "2026-09-03T09:15:00+08:00",
};
const INCIDENT_REPLACEMENT = {
  booking: { booking_reference: INCIDENT_SCENARIO.booking.reference, customer_name: INCIDENT_SCENARIO.affectedRenter.name, current_vehicle: INCIDENT_SCENARIO.damagedVehicle, start_datetime: INCIDENT_BOOKING.start_datetime, end_datetime: INCIDENT_BOOKING.end_datetime },
  candidates: [{ ...INCIDENT_SCENARIO.replacementVehicle, availability: "available", availability_label: "Available", category_match: false, vehicle_warnings: [], conflicts: { has_critical: false } }],
};
const INCIDENT_CONFIRMATION = {
  booking: INCIDENT_BOOKING, company: INCIDENT_SCENARIO.business, vehicle: INCIDENT_SCENARIO.replacementVehicle,
  expired: false, token_status: "active", terms: null, damages: [],
  version_diff: { changed_fields: ["item_id"], previous_confirmed_snapshot: { vehicle_name: "Kia Rio (PYR-5502)" }, current_snapshot: { vehicle_name: "Toyota Montero (KDQ-7710)" } },
};

function IncidentChatLiveScreen({ data, onAction }) {
  return <div className="mx-auto max-w-md py-5" data-testid={`incident-chat-${data.kind}`}>
    <ChatSimulation script={data.messages} reducedMotion />
    <button type="button" onClick={() => onAction?.()} data-testid="incident-chat-continue" className="mt-3 w-full rounded-lg bg-[#1B4332] py-2.5 text-sm font-semibold text-white">Continue</button>
  </div>;
}

// "incident" mode: the vehicle is still Available, about to be opened for
// Log incident. "status-updated" mode: the incident was just recorded (see
// IncidentLogLiveScreen), so this re-render of the SAME real vehicle
// overview shows the resulting real status - Damaged, per the canonical
// accident -> damaged mapping - plus the incident that produced it, before
// the affected-booking warning is allowed to appear. "recovery" mode is the
// later return-to-service step in the standalone scenario's deeper tail.
const INCIDENT_AWARE_MODES = new Set(["incident", "status-updated"]);

function VehicleCommandLiveScreen({ data, onAction }) {
  const [status, setStatus] = useState(data.status);
  const summary = {
    bookings: INCIDENT_AWARE_MODES.has(data.mode) ? { upcoming_count: 1, upcoming_next: { id: "vb-1", customer_name: "Anna Reyes", start_datetime: INCIDENT_BOOKING.start_datetime }, completed_count: 18 } : { completed_count: 19 },
    damages: { open: (data.mode === "recovery" || data.mode === "status-updated") ? 1 : 0, total: data.mode === "recovery" ? 2 : 1 },
    incidents: { open: INCIDENT_AWARE_MODES.has(data.mode) ? 1 : 0 },
    maintenance: { pending: INCIDENT_AWARE_MODES.has(data.mode) ? 1 : 0 }, documents: { total_with_expiry: 3 }, inspections: { total: data.mode === "recovery" ? 1 : 0, last: data.mode === "recovery" ? { kind: "clearance", status: "finalized" } : null }, notes: { total: 1, pinned: 1 },
  };
  // "incident" mode's action opens Log incident (a real navigation-style
  // beat, handled by advancing to the next step - IncidentLogLiveScreen -
  // rather than changing status here directly, since in production the
  // status change is a SIDE EFFECT of recording the incident, not a
  // standalone action on this screen). "recovery" mode still changes status
  // itself (returning to service has no separate incident-recording step).
  // "status-updated" mode's status is already final: this button is just
  // "Continue" to the next beat.
  const act = () => {
    if (data.mode === "recovery") setStatus("Available");
    setTimeout(onAction, 350);
  };
  return (
    <div className="space-y-4" data-testid="incident-vehicle-command">
      <div className="flex flex-wrap items-start justify-between gap-3 rounded-xl border border-slate-200 bg-white p-4">
        <div><h2 className="font-display text-xl font-semibold">Kia Rio</h2><p className="font-mono text-xs text-slate-500">PYR-5502</p></div>
        <span className={`rounded-full border px-3 py-1 text-xs font-semibold ${status === "Available" ? "border-emerald-200 bg-emerald-50 text-emerald-800" : status === "Damaged" ? "border-red-200 bg-red-50 text-red-800" : "border-amber-200 bg-amber-50 text-amber-800"}`}>{status}</span>
      </div>
      {data.mode === "status-updated" && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900" data-testid="incident-status-updated-banner">
          <b>Status updated.</b> Status: {status} · Incident: {INCIDENT_TYPE_META[data.incidentType] || data.incidentType} · Severity: <SeverityBadge severity={data.severity} />
        </div>
      )}
      <VehicleOverviewTab v={{ ...INCIDENT_SCENARIO.damagedVehicle, status, notes: data.mode === "recovery" ? INCIDENT_SCENARIO.remainingDamageNote : INCIDENT_SCENARIO.accidentNote }} summary={summary} onTabChange={() => {}} />
      <button type="button" onClick={act} data-testid="incident-vehicle-status-action" className="w-full rounded-lg bg-[#1B4332] py-2.5 text-sm font-semibold text-white">{data.action}</button>
    </div>
  );
}

/**
 * The real incident-entry taxonomy (lib/incidents.jsx's INCIDENT_TYPE_META /
 * SEVERITY_META - the exact same constants IncidentNew.jsx uses to build its
 * real "Log an incident" form) walked as two selectable phases: type, then
 * severity, then a confirm/record beat. Production puts type and severity on
 * ONE real form together (plus an "also update vehicle status" option that
 * defaults on): this mirrors that real shape rather than inventing a second
 * incident-entry system, while still giving the walkthrough a clear,
 * separate "select accident -> select severity -> record" feel. Only the
 * scenario's own deterministic choices (Accident / Critical) actually
 * advance: the full real list is shown so the taxonomy reads as genuine and
 * complete, not cherry-picked, but every other option is disabled with the
 * same "not part of this demo scenario" convention already used elsewhere
 * (see DemoWorkflow.jsx's disabled Add-booking button).
 */
function IncidentLogLiveScreen({ onAction }) {
  const [phase, setPhase] = useState("type");
  const CANONICAL_TYPE = "accident";
  const CANONICAL_SEVERITY = "critical";

  if (phase === "type") {
    return (
      <div className="space-y-4" data-testid="incident-log-type">
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="mb-1 text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Kia Rio · PYR-5502</div>
          <h2 className="font-display text-lg font-semibold mb-3">Log incident: incident type</h2>
          <div className="grid grid-cols-2 gap-2">
            {Object.entries(INCIDENT_TYPE_META).map(([key, label]) => {
              const canonical = key === CANONICAL_TYPE;
              return (
                <button
                  key={key} type="button"
                  data-testid={`incident-type-option-${key}`}
                  disabled={!canonical}
                  title={canonical ? undefined : "Not part of this demo scenario"}
                  onClick={() => setPhase("severity")}
                  className={`rounded-lg border px-3 py-2.5 text-sm font-medium text-left transition-colors ${
                    canonical ? "border-red-300 bg-red-50 text-red-800 hover:bg-red-100" : "border-slate-200 bg-white text-slate-400 cursor-not-allowed"
                  }`}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  if (phase === "severity") {
    return (
      <div className="space-y-4" data-testid="incident-log-severity">
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="mb-1 text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Incident type: {INCIDENT_TYPE_META[CANONICAL_TYPE]}</div>
          <h2 className="font-display text-lg font-semibold mb-3">Severity</h2>
          <div className="flex flex-wrap gap-2">
            {Object.entries(SEVERITY_META).map(([key, meta]) => {
              const canonical = key === CANONICAL_SEVERITY;
              return (
                <button
                  key={key} type="button"
                  data-testid={`incident-severity-option-${key}`}
                  disabled={!canonical}
                  title={canonical ? undefined : "Not part of this demo scenario"}
                  onClick={() => setPhase("confirm")}
                  className={`rounded-full border px-4 py-2 text-sm font-semibold transition-colors ${canonical ? meta.cls : "border-slate-200 bg-white text-slate-300 cursor-not-allowed"}`}
                >
                  {meta.label}
                </button>
              );
            })}
          </div>
          <button type="button" onClick={() => setPhase("type")} data-testid="incident-log-back-to-type" className="mt-3 text-xs text-slate-500 underline">
            ← Back to incident type
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="incident-log-confirm">
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <h2 className="font-display text-lg font-semibold mb-3">Confirm incident</h2>
        <dl className="grid grid-cols-2 gap-3 text-sm">
          <div><dt className="text-xs font-semibold uppercase text-slate-500">Vehicle</dt><dd>Kia Rio · PYR-5502</dd></div>
          <div><dt className="text-xs font-semibold uppercase text-slate-500">Incident type</dt><dd>{INCIDENT_TYPE_META[CANONICAL_TYPE]}</dd></div>
          <div><dt className="text-xs font-semibold uppercase text-slate-500">Severity</dt><dd><SeverityBadge severity={CANONICAL_SEVERITY} /></dd></div>
          <div><dt className="text-xs font-semibold uppercase text-slate-500">Update vehicle status</dt><dd>Yes: Accident → Damaged</dd></div>
        </dl>
      </div>
      <button
        type="button"
        onClick={() => onAction({ incidentType: CANONICAL_TYPE, severity: CANONICAL_SEVERITY })}
        data-testid="incident-log-record"
        className="w-full rounded-lg bg-[#1B4332] py-2.5 text-sm font-semibold text-white"
      >
        Record Incident
      </button>
    </div>
  );
}

function DamageRecordLiveScreen({ data, onAction }) {
  // Only used for the "remaining" (post-repair) stage now: the original
  // accident-logging moment lives in IncidentLogLiveScreen below, which
  // records the real incident type/severity instead of just a damage-map
  // entry - see the custom.js comment at the incident-log-live step.
  const damage = { id: data.stage === "remaining" ? "damage-remains" : "damage-impact", view: "front", x_pct: 31, y_pct: 43, tag_code: data.stage === "remaining" ? "big_scratch" : "big_dent", tag_label: data.stage === "remaining" ? "Remaining cosmetic damage" : "Front-left impact", severity: data.stage === "remaining" ? "medium" : "big", status: "active" };
  return (
    <div className="space-y-4" data-testid="incident-damage-record">
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="mb-3"><h2 className="font-display text-lg font-semibold">Kia Rio · damage sheet</h2><p className="text-xs text-slate-500">PYR-5502 · {data.stage === "remaining" ? "History preserved after repair" : "Reported by Rafael Uy"}</p></div>
        <VehicleDamageMap view="front" damages={[damage]} selectedId={damage.id} onSelect={() => {}} readOnly />
        <dl className="mt-3 grid gap-2 text-sm sm:grid-cols-2"><div><dt className="text-xs font-semibold uppercase text-slate-500">Damage</dt><dd>{damage.tag_label}</dd></div><div><dt className="text-xs font-semibold uppercase text-slate-500">Status</dt><dd>Active · kept in history</dd></div><div className="sm:col-span-2"><dt className="text-xs font-semibold uppercase text-slate-500">Notes</dt><dd>{data.stage === "remaining" ? INCIDENT_SCENARIO.remainingDamageNote : INCIDENT_SCENARIO.accidentNote}</dd></div></dl>
      </div>
      <button type="button" onClick={onAction} data-testid="incident-damage-save" className="w-full rounded-lg bg-[#1B4332] py-2.5 text-sm font-semibold text-white">{data.stage === "remaining" ? "Save remaining damage to history" : "Save damage record"}</button>
    </div>
  );
}

function WarningLiveScreen({ onAction }) {
  return <div className="mx-auto max-w-xl" data-testid="incident-warning-live"><WarningCard w={INCIDENT_WARNING} onChanged={onAction} onAction={async () => ({})} reassignPreloaded={INCIDENT_REPLACEMENT} onReassignSubmit={async () => ({ new_vehicle: INCIDENT_SCENARIO.replacementVehicle })} /></div>;
}

function WarningReassignLiveScreen({ onAction }) {
  const [noReplacement, setNoReplacement] = useState(false);
  const [cancelled, setCancelled] = useState(false);
  if (cancelled) return <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="incident-cancelled"><h2 className="font-display text-xl font-semibold">Booking cancelled · TCR-4821</h2><p className="mt-2 text-sm text-slate-600">Anna receives a customer-safe cancellation status update. No automatic refund, compensation, or replacement is promised.</p><button type="button" onClick={() => onAction({ demoOutcome: "cancelled" })} data-testid="incident-cancel-continue" className="mt-4 rounded-lg bg-[#1B4332] px-4 py-2 text-sm font-semibold text-white">Finish cancellation branch</button></div>;
  if (noReplacement) return <div className="rounded-xl border border-amber-200 bg-white p-6" data-testid="incident-no-replacement"><h2 className="font-display text-xl font-semibold">No suitable replacement available</h2><p className="mt-2 text-sm text-slate-600">Contact Anna and use the booking's cancel/change options. ConfirmPH does not promise a refund or compensation.</p><div className="mt-4 flex flex-wrap gap-2"><button type="button" onClick={() => setNoReplacement(false)} className="rounded-lg border px-4 py-2 text-sm">Back to candidates</button><button type="button" onClick={() => setCancelled(true)} data-testid="incident-cancel-booking" className="rounded-lg bg-rose-700 px-4 py-2 text-sm font-semibold text-white">Cancel booking and notify Anna</button></div></div>;
  return <div data-testid="incident-reassign-live"><ReassignVehicleModal bookingId="vb-1" open onClose={() => setNoReplacement(true)} onDone={onAction} preloaded={INCIDENT_REPLACEMENT} onSubmit={async () => ({ new_vehicle: INCIDENT_SCENARIO.replacementVehicle })} onNoSuitableReplacement={() => setNoReplacement(true)} /></div>;
}

function ReconfirmationLiveScreen({ onAction }) {
  return <div className="rounded-xl border border-amber-300 bg-amber-50 p-5" data-testid="incident-reconfirmation"><h2 className="font-display text-lg font-semibold text-amber-950">Booking details changed since Anna last confirmed</h2><p className="mt-1 text-sm text-amber-900">Kia Rio → Toyota Montero is a material change. The old confirmation remains in history until Anna explicitly accepts.</p><button type="button" onClick={onAction} data-testid="incident-send-reconfirmation" className="mt-4 rounded-lg bg-amber-700 px-4 py-2 text-sm font-semibold text-white">Send Updated Confirmation</button></div>;
}

function CustomerConfirmationLiveScreen({ onAction }) {
  return <CustomerConfirmation preloaded={INCIDENT_CONFIRMATION} onAction={(action) => { if (action === "accept_changes") setTimeout(onAction, 900); return { status: action === "accept_changes" ? "confirmed" : "change_requested" }; }} />;
}

function BusinessResolutionLiveScreen({ onAction, isLast }) {
  const preloaded = { booking: { ...INCIDENT_BOOKING, status: "confirmed", reconfirmation_required: false, item_id: INCIDENT_SCENARIO.replacementVehicle.id }, company: INCIDENT_SCENARIO.business, vehicle: INCIDENT_SCENARIO.replacementVehicle, confirmation_versions: [{ id: "v1", version_number: 1, trigger: "booking_created", confirmed_at: "2026-09-01T10:00:00+08:00", confirmed_by_type: "customer" }, { id: "v2", version_number: 2, trigger: "vehicle_reassigned", confirmed_at: "2026-09-03T10:00:00+08:00", confirmed_by_type: "customer" }], deposit: { status: "pending", required: 2000, collected: 0 }, review_status: { invited: false } };
  // isLast is true on the homepage-embedded walkthrough, which stops right
  // here (see vehicleBreakdownDetailed.embedStopAtScreen): the deeper
  // workshop/repair/inspection tail only exists on the standalone scenario
  // page, so the button copy shouldn't promise a "continue" that doesn't
  // happen in this context.
  return <div data-testid="incident-business-resolution"><div className="mb-3 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900"><b>Warning resolved.</b> Anna accepted Toyota Montero · KDQ-7710.</div><CustomerBookingDetail preloaded={preloaded} /><button type="button" onClick={onAction} data-testid="incident-resolution-continue" className="mt-4 rounded-lg bg-[#1B4332] px-4 py-2 text-sm font-semibold text-white">{isLast ? "Replacement confirmed" : "Continue to vehicle recovery"}</button></div>;
}

// -----------------------------------------------------------------
// Small building blocks (compact clones of real ConfirmPH styles)
// -----------------------------------------------------------------
function PageHeader({ icon: Icon, title, subtitle, right }) {
  return (
    <div className="flex items-end justify-between gap-3 mb-4 flex-wrap">
      <div>
        <h1 className="font-display text-xl sm:text-2xl font-bold text-slate-900 inline-flex items-center gap-2">
          {Icon && <Icon className="w-5 h-5 text-[#1B4332]" />}
          {title}
        </h1>
        {subtitle && <p className="text-xs text-slate-600 mt-0.5">{subtitle}</p>}
      </div>
      {right}
    </div>
  );
}
function StatCard({ label, value, tone = "slate" }) {
  const tones = {
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-900",
    amber:   "border-amber-200 bg-amber-50 text-amber-900",
    rose:    "border-rose-200 bg-rose-50 text-rose-900",
    sky:     "border-sky-200 bg-sky-50 text-sky-900",
    slate:   "border-slate-200 bg-white text-slate-800",
  };
  return (
    <div className={`rounded-lg border p-3 ${tones[tone] || tones.slate}`}>
      <div className="text-[10px] uppercase tracking-[0.18em] font-semibold opacity-70">{label}</div>
      <div className="text-2xl font-display font-semibold mt-0.5">{value}</div>
    </div>
  );
}
function StatusPill({ children, tone = "slate" }) {
  const tones = {
    emerald: "bg-emerald-100 text-emerald-800 border-emerald-200",
    amber:   "bg-amber-100 text-amber-800 border-amber-200",
    rose:    "bg-rose-100 text-rose-800 border-rose-200",
    sky:     "bg-sky-100 text-sky-800 border-sky-200",
    slate:   "bg-slate-100 text-slate-700 border-slate-200",
  };
  return <span className={`inline-flex items-center text-[10px] px-2 py-0.5 rounded-full font-medium border ${tones[tone]}`}>{children}</span>;
}
function pillToneForStatus(s) {
  const t = (s || "").toLowerCase();
  if (/confirmed|paid|resolved|available|completed/.test(t)) return "emerald";
  if (/overdue|declined|failed|critical|damage/.test(t))    return "rose";
  if (/pending|await|risk|maintenance|reviewed|change/.test(t)) return "amber";
  return "slate";
}

// -----------------------------------------------------------------
// SCREEN: Bookings list  (mirrors Bookings.jsx card layout)
// -----------------------------------------------------------------
function BookingsListScreen({ data, onAction }) {
  const { bookings = [], focusId, cta } = data;
  return (
    <div data-testid="screen-bookings-list">
      <PageHeader icon={ClipboardList} title="Bookings" subtitle="All bookings across your fleet"
                   right={<button disabled className="text-xs px-3 py-1.5 rounded-lg bg-[#1B4332] text-white opacity-60">+ New booking</button>} />
      <ul className="space-y-2">
        {bookings.map((b) => (
          <li key={b.id}
              className={`rounded-xl border bg-white p-3 flex items-center justify-between gap-3 ${b.id === focusId ? "border-emerald-400 ring-2 ring-emerald-200" : "border-slate-200"}`}
              data-testid={`bookings-list-row-${b.id}`}>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold text-slate-900 text-sm truncate">{b.customer}</span>
                <StatusPill tone={pillToneForStatus(b.status)}>{b.status}</StatusPill>
                {b.source && <span className="text-[10px] text-slate-500">via {b.source}</span>}
              </div>
              <div className="text-xs text-slate-600 mt-0.5 flex items-center gap-1"><Car className="w-3 h-3" />{b.vehicle}</div>
              <div className="text-[11px] text-slate-500 mt-0.5">{b.pickup} → {b.ret}</div>
            </div>
            {b.id === focusId && (
              <button onClick={onAction} data-testid={`bookings-open-${b.id}`}
                      className="text-xs px-3 py-1.5 rounded-lg border border-emerald-300 text-emerald-800 bg-emerald-50 font-medium">
                Open
              </button>
            )}
          </li>
        ))}
      </ul>
      {cta && (
        <div className="mt-3 text-[11px] text-slate-500">Tap <b>Open</b> on the highlighted booking to continue.</div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------
// SCREEN: Booking detail  (mirrors BookingDetail.jsx layout)
// -----------------------------------------------------------------
function BookingDetailScreen({ data, onAction }) {
  const { booking = {}, banners = [], primaryAction, timeline = [], deposit } = data;
  return (
    <div data-testid="screen-booking-detail" className="space-y-4">
      <button className="text-xs text-slate-500 inline-flex items-center gap-1">
        <ChevronLeft className="w-3.5 h-3.5" /> Back to Bookings
      </button>
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="flex items-start justify-between gap-2 flex-wrap">
          <div>
            <h2 className="font-display text-lg font-semibold text-slate-900">{booking.customer}</h2>
            <div className="text-xs text-slate-500">Booking #{booking.id}</div>
          </div>
          <StatusPill tone={pillToneForStatus(booking.status)}>{booking.status}</StatusPill>
        </div>
        <dl className="mt-3 grid grid-cols-2 gap-3 text-sm">
          <Field label="Vehicle" value={booking.vehicle} />
          <Field label="Source" value={booking.source} />
          <Field label="Pickup" value={booking.pickup} />
          <Field label="Return" value={booking.ret} />
          {booking.price   && <Field label="Rate"     value={booking.price} />}
          {booking.subtotal&& <Field label="Subtotal" value={booking.subtotal} />}
          {booking.deposit && <Field label="Deposit"  value={booking.deposit} />}
          {booking.balance && <Field label="Balance"  value={booking.balance} />}
        </dl>
      </div>

      {banners.map((b, i) => (
        <div key={i}
             className={`rounded-xl border p-3 text-xs ${b.tone === "warn" ? "border-amber-300 bg-amber-50 border-l-4 border-l-amber-500 text-amber-900" : b.tone === "danger" ? "border-rose-300 bg-rose-50 border-l-4 border-l-rose-500 text-rose-900" : "border-sky-200 bg-sky-50 text-sky-900"}`}
             data-testid={`booking-banner-${i}`}>
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <div>
              <div className="font-semibold">{b.title}</div>
              {b.text && <div className="mt-0.5">{b.text}</div>}
            </div>
          </div>
        </div>
      ))}

      {deposit && (
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Deposit</div>
          <div className="mt-1 text-sm">
            Recorded: <span className="font-mono">{deposit.recorded}</span> · Balance due: <span className="font-mono">{deposit.balance}</span>
          </div>
          {deposit.note && <div className="text-xs text-slate-600 mt-1">{deposit.note}</div>}
        </div>
      )}

      {timeline.length > 0 && (
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="flex items-center gap-2 mb-2">
            <History className="w-4 h-4 text-slate-500" />
            <div className="font-display text-base font-semibold">Timeline</div>
          </div>
          <ol className="space-y-2 text-sm" data-testid="booking-detail-timeline">
            {timeline.map((e, i) => (
              <li key={i} className="border-l-2 border-slate-200 pl-3">
                <div className="font-medium text-slate-900">{e.what}</div>
                <div className="text-[11px] text-slate-500">{e.at} · {e.by}</div>
              </li>
            ))}
          </ol>
        </div>
      )}

      {primaryAction && (
        <button onClick={onAction} data-testid="booking-primary-action"
                className="w-full rounded-lg bg-[#1B4332] text-white text-sm font-medium py-2.5">
          {primaryAction}
        </button>
      )}
    </div>
  );
}
function Field({ label, value }) {
  if (!value) return null;
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-slate-500">{label}</div>
      <div className="text-slate-900 mt-0.5">{value}</div>
    </div>
  );
}

// -----------------------------------------------------------------
// SCREEN: Edit booking dates  (mirrors the real edit modal)
// -----------------------------------------------------------------
function EditBookingDatesScreen({ data, onAction }) {
  const [pickup, setPickup] = useState(data.pickup);
  const [ret, setRet] = useState(data.ret);
  return (
    <div data-testid="screen-edit-dates" className="space-y-3">
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="text-sm font-semibold text-slate-900 mb-2">Edit booking dates</div>
        <div className="text-xs text-slate-600 mb-3">Customer: {data.customer} · Vehicle: {data.vehicle}</div>
        <div className="grid grid-cols-2 gap-3">
          <label className="text-xs">
            <span className="uppercase tracking-wider font-semibold text-slate-500">Pickup</span>
            <input value={pickup} onChange={e => setPickup(e.target.value)}
                   data-testid="edit-dates-pickup"
                   className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 text-sm" />
          </label>
          <label className="text-xs">
            <span className="uppercase tracking-wider font-semibold text-slate-500">Return</span>
            <input value={ret} onChange={e => setRet(e.target.value)}
                   data-testid="edit-dates-return"
                   className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 text-sm" />
          </label>
        </div>
        <div className="mt-3 rounded-md bg-sky-50 border border-sky-200 text-sky-900 p-2 text-xs">
          Save will re-run conflict checks and produce a fresh confirmation link for the customer.
        </div>
      </div>
      <button onClick={onAction} data-testid="edit-dates-save"
              className="w-full rounded-lg bg-[#1B4332] text-white text-sm font-medium py-2.5">
        Save & re-check conflict
      </button>
    </div>
  );
}

// -----------------------------------------------------------------
// SCREEN: Find booking (search)
// -----------------------------------------------------------------
function FindBookingScreen({ data, onAction }) {
  const [q, setQ] = useState(data.prefillQuery || "");
  return (
    <div data-testid="screen-find-booking" className="space-y-3">
      <PageHeader icon={Search} title="Find booking" subtitle="Search by name, phone, plate or booking ID" />
      <div className="rounded-xl border border-slate-200 bg-white p-3">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)}
                 data-testid="find-booking-input"
                 className="w-full pl-9 pr-3 py-2 rounded-lg border border-slate-300 outline-none focus:border-[#2D6A4F] text-sm" />
        </div>
      </div>
      <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-900" data-testid="find-booking-empty">
        <div className="font-semibold">No booking found for &ldquo;{q}&rdquo;.</div>
        <div className="text-xs mt-1">The conversation with this customer was never recorded in ConfirmPH.</div>
      </div>
      <button onClick={onAction} data-testid="find-booking-recover"
              className="w-full rounded-lg bg-[#1B4332] text-white text-sm font-medium py-2.5">
        Recover the booking into the Notepad now
      </button>
    </div>
  );
}

// -----------------------------------------------------------------
// SCREEN: Notepad recover (mirrors StaffNotepad.jsx shape)
// -----------------------------------------------------------------
function NotepadRecoverScreen({ data, onAction }) {
  const [text, setText] = useState(data.suggested || "");
  return (
    <div data-testid="screen-notepad-recover" className="space-y-3">
      <PageHeader icon={ClipboardList} title="Booking Notepad" subtitle="Capture the details verbally before you assign a vehicle." />
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <textarea value={text} onChange={(e) => setText(e.target.value)}
                  data-testid="notepad-recover-textarea"
                  rows={5}
                  className="w-full rounded-lg border border-slate-300 p-3 text-sm focus:border-[#2D6A4F] outline-none" />
        <div className="mt-3 flex items-center gap-2">
          <select className="h-9 rounded-lg border border-slate-300 bg-white px-2 text-xs" data-testid="notepad-source">
            <option>Walk-in / recovered</option>
          </select>
          <button onClick={onAction} data-testid="notepad-recover-convert"
                  className="ml-auto inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#1B4332] text-white text-xs font-medium">
            <ArrowRight className="w-3.5 h-3.5" /> Convert to Booking Draft
          </button>
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------
// SCREEN: Reminders/Follow-ups list (mirrors Reminders.jsx)
// -----------------------------------------------------------------
function RemindersListScreen({ data, onAction }) {
  const { items = [], focusId } = data;
  return (
    <div data-testid="screen-reminders-list" className="space-y-3">
      <PageHeader icon={Bell} title="Follow-ups" subtitle="Automated nudges for stale confirmations, pickups, incidents."
                   right={<button disabled className="text-xs px-3 py-1.5 rounded-lg bg-[#1B4332] text-white opacity-60 inline-flex items-center gap-1"><RefreshCw className="w-3 h-3" /> Run now</button>} />
      <div className="grid grid-cols-4 gap-2">
        <StatCard label="Critical" value={items.filter(i => i.priority === "critical").length} tone="rose" />
        <StatCard label="High"     value={items.filter(i => i.priority === "high").length} tone="amber" />
        <StatCard label="Medium"   value={items.filter(i => i.priority === "medium").length} tone="amber" />
        <StatCard label="Low"      value={items.filter(i => i.priority === "low").length} tone="slate" />
      </div>
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <ul className="divide-y divide-slate-100">
          {items.map((r) => (
            <li key={r.id}
                className={`p-3 ${r.id === focusId ? "bg-emerald-50" : "hover:bg-slate-50"}`}
                data-testid={`reminder-row-${r.id}`}>
              <div className="flex items-start justify-between gap-2 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <StatusPill tone={r.priority === "critical" ? "rose" : r.priority === "high" ? "amber" : "slate"}>{r.priority}</StatusPill>
                    <StatusPill tone={r.status === "sent" ? "emerald" : "amber"}>{r.status}</StatusPill>
                    <span className="text-[11px] text-slate-500">{r.type}</span>
                  </div>
                  <div className="font-medium text-slate-900 mt-1 text-sm">{r.title}</div>
                  <div className="text-xs text-slate-600 mt-0.5">{r.message}</div>
                  <div className="text-[11px] text-slate-500 mt-1">To: <span className="font-mono">{r.to}</span> · Due: {r.due}</div>
                </div>
                {r.id === focusId && r.status === "pending" && (
                  <div className="flex gap-1.5">
                    <button onClick={onAction} data-testid={`reminder-send-${r.id}`}
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-[#1B4332] text-white text-xs font-medium">
                      <Send className="w-3 h-3" /> Send now
                    </button>
                    <button disabled className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg border border-slate-300 bg-white text-xs opacity-70">
                      <SkipForward className="w-3 h-3" /> Skip
                    </button>
                  </div>
                )}
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------
// SCREEN: Warnings list (mirrors Warnings.jsx)
// -----------------------------------------------------------------
function WarningsListScreen({ data, onAction }) {
  const { items = [], focusId } = data;
  return (
    <div data-testid="screen-warnings-list" className="space-y-3">
      <PageHeader icon={AlertTriangle} title="Warning Center" subtitle="Operational risks across bookings, vehicles, and incidents."
                   right={<button disabled className="text-xs px-3 py-1.5 rounded-lg border border-slate-300 bg-white opacity-60 inline-flex items-center gap-1"><RefreshCw className="w-3 h-3" /> Refresh warnings</button>} />
      <div className="grid grid-cols-4 gap-2">
        <StatCard label="Critical" value={items.filter(i => i.severity === "critical").length} tone="rose" />
        <StatCard label="High"     value={items.filter(i => i.severity === "high").length} tone="amber" />
        <StatCard label="Medium"   value={items.filter(i => i.severity === "medium").length} tone="amber" />
        <StatCard label="Low"      value={items.filter(i => i.severity === "low").length} tone="slate" />
      </div>
      <ul className="space-y-2">
        {items.map((w) => {
          const focused = w.id === focusId;
          const sev = w.severity;
          const tone = sev === "critical" ? "border-rose-300 bg-rose-50" :
                        sev === "high"     ? "border-amber-300 bg-amber-50" :
                                              "border-slate-200 bg-white";
          return (
            <li key={w.id} className={`rounded-xl border p-3 ${tone} ${focused ? "ring-2 ring-emerald-300" : ""}`}
                data-testid={`warning-row-${w.id}`}>
              <div className="flex items-start justify-between gap-2 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <StatusPill tone={sev === "critical" ? "rose" : "amber"}>{sev}</StatusPill>
                    <span className="text-[11px] text-slate-500">{w.type}</span>
                    <StatusPill tone={w.status === "active" ? "rose" : "slate"}>{w.status}</StatusPill>
                  </div>
                  <div className="font-semibold text-slate-900 mt-1 text-sm">{w.title}</div>
                  <div className="text-xs text-slate-600 mt-0.5">{w.message}</div>
                  {w.recommended && <div className="text-xs text-slate-600 mt-1"><b>Recommended:</b> {w.recommended}</div>}
                </div>
              </div>
              {focused && (
                <div className="mt-2 flex gap-1.5 flex-wrap">
                  <button onClick={onAction} data-testid={`warning-primary-${w.id}`}
                          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-[#1B4332] text-white text-xs font-medium">
                    <ArrowRightLeft className="w-3 h-3" /> {w.primary || "Take action"}
                  </button>
                  <button disabled className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg border border-slate-300 bg-white text-xs opacity-70">
                    <Eye className="w-3 h-3" /> Review
                  </button>
                  <button disabled className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg border border-slate-300 bg-white text-xs opacity-70">
                    <XCircle className="w-3 h-3" /> Close
                  </button>
                  <button disabled className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg border border-slate-300 bg-white text-xs opacity-70">
                    <MessageSquare className="w-3 h-3" /> Add note
                  </button>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// -----------------------------------------------------------------
// SCREEN: Activity timeline (mirrors BookingTimeline)
// -----------------------------------------------------------------
function ActivityTimelineScreen({ data }) {
  const { title = "Activity", subject, events = [] } = data;
  return (
    <div data-testid="screen-activity" className="space-y-3">
      <PageHeader icon={ActivityIcon} title={title} subtitle={subject} />
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <ol className="space-y-2.5">
          {events.map((e, i) => (
            <li key={i} className="border-l-2 border-slate-200 pl-3 py-0.5" data-testid={`activity-event-${i}`}>
              <div className="text-sm font-medium text-slate-900">{e.what}</div>
              <div className="text-[11px] text-slate-500">{e.at} · <span className="text-slate-700 font-medium">{e.by}</span>{e.role ? ` (${e.role})` : ""}</div>
              {e.detail && <div className="text-xs text-slate-600 mt-0.5">{e.detail}</div>}
            </li>
          ))}
        </ol>
      </div>
      <div className="text-[11px] text-slate-500 italic">
        Every action is attributed to a specific staff account. Perfect for owners who ask &ldquo;who did what?&rdquo;
      </div>
    </div>
  );
}

// -----------------------------------------------------------------
// SCREEN: Employees list (mirrors Employees.jsx)
// -----------------------------------------------------------------
const PERMISSIONS = [
  ["bookings",  "Bookings"],
  ["vehicles",  "Vehicles"],
  ["incidents", "Incidents"],
  ["reminders", "Reminders"],
  ["reports",   "Reports & audit"],
  ["settings",  "Company settings"],
];
function EmployeesListScreen({ data, onAction }) {
  const { employees = [], focusId, ctaLabel = "Add employee" } = data;
  return (
    <div data-testid="screen-employees-list" className="space-y-3">
      <PageHeader icon={Users} title="Employees" subtitle="Add staff, set permissions, and track activity."
                   right={<button onClick={onAction} data-testid="employees-add-btn"
                                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#1B4332] text-white text-xs font-medium">
                     <Plus className="w-3.5 h-3.5" /> {ctaLabel}
                   </button>} />
      <div className="grid sm:grid-cols-2 gap-3">
        {employees.map((e) => (
          <div key={e.id} className={`rounded-xl border ${e.id === focusId ? "border-emerald-400 ring-2 ring-emerald-200" : "border-slate-200"} bg-white p-3`}
               data-testid={`employee-card-${e.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="text-sm font-semibold text-slate-900">{e.name}</div>
                <div className="text-[11px] text-slate-500">{e.title}</div>
                <div className="text-[11px] text-slate-500 font-mono">{e.login}</div>
              </div>
              <StatusPill tone={e.active === false ? "slate" : "emerald"}>
                {e.active === false ? "Deactivated" : "Active"}
              </StatusPill>
            </div>
            <div className="mt-2">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Permissions</div>
              <div className="mt-1 flex flex-wrap gap-1">
                {(e.permissions || []).map((p) => (
                  <span key={p} className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-700">{p}</span>
                ))}
              </div>
            </div>
            <div className="mt-2 flex flex-wrap gap-1.5">
              <button disabled className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md border border-slate-300 opacity-70"><Edit className="w-3 h-3" /> Edit</button>
              <button disabled className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md border border-slate-300 opacity-70"><ActivityIcon className="w-3 h-3" /> Work Summary</button>
              {e.active !== false ? (
                <button disabled className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md border border-rose-300 text-rose-700 opacity-70"><PowerOff className="w-3 h-3" /> Deactivate</button>
              ) : (
                <button disabled className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md border border-emerald-300 text-emerald-700 opacity-70"><Power className="w-3 h-3" /> Reactivate</button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// -----------------------------------------------------------------
// SCREEN: Employee form (mirrors EmployeeFormModal)
// -----------------------------------------------------------------
function EmployeeFormScreen({ data, onAction }) {
  const [name, setName] = useState(data.name || "");
  const [email, setEmail] = useState(data.email || "");
  const [perms, setPerms] = useState(data.permissions || ["bookings"]);
  const toggle = (k) => setPerms((p) => p.includes(k) ? p.filter(x => x !== k) : [...p, k]);
  return (
    <div data-testid="screen-employee-form" className="space-y-3">
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="text-sm font-semibold text-slate-900 mb-3">Add employee</div>
        <div className="grid sm:grid-cols-2 gap-3 text-xs">
          <label>
            <span className="uppercase tracking-wider font-semibold text-slate-500">Full name</span>
            <input value={name} onChange={(e)=>setName(e.target.value)} data-testid="emp-form-name"
                   className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 text-sm" />
          </label>
          <label>
            <span className="uppercase tracking-wider font-semibold text-slate-500">Job title</span>
            <input defaultValue={data.title || ""} data-testid="emp-form-title"
                   className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 text-sm" />
          </label>
          <label>
            <span className="uppercase tracking-wider font-semibold text-slate-500">Email</span>
            <input value={email} onChange={(e)=>setEmail(e.target.value)} data-testid="emp-form-email"
                   className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 text-sm" />
          </label>
          <label>
            <span className="uppercase tracking-wider font-semibold text-slate-500">Password</span>
            <input type="password" defaultValue="••••••" data-testid="emp-form-password"
                   className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 text-sm" />
          </label>
        </div>
        <div className="mt-4">
          <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-slate-500 mb-1">Permissions</div>
          <div className="grid sm:grid-cols-2 gap-2">
            {PERMISSIONS.map(([k, l]) => (
              <label key={k} className={`flex items-center gap-2 p-2 rounded-lg border text-sm ${perms.includes(k) ? "border-emerald-300 bg-emerald-50" : "border-slate-200 bg-white"}`}>
                <input type="checkbox" checked={perms.includes(k)} onChange={() => toggle(k)}
                       data-testid={`emp-perm-${k}`} />
                {l}
              </label>
            ))}
          </div>
          <p className="mt-2 text-[11px] text-slate-500 flex items-center gap-1">
            <KeyRound className="w-3 h-3" /> Employees only see and act on what you allow here.
          </p>
        </div>
      </div>
      <button onClick={onAction} data-testid="emp-form-submit"
              className="w-full rounded-lg bg-[#1B4332] text-white text-sm font-medium py-2.5">
        Create employee
      </button>
    </div>
  );
}

// -----------------------------------------------------------------
// SCREEN: Employee performance (mirrors PerformanceModal)
// -----------------------------------------------------------------
function EmployeePerformanceScreen({ data }) {
  const { name, login, metrics = {} } = data;
  const entries = [
    ["Bookings created",   metrics.bookings_created],
    ["Bookings completed", metrics.bookings_completed],
    ["Bookings cancelled", metrics.bookings_cancelled],
    ["Incidents resolved", metrics.incidents_resolved],
    ["Verifications",       metrics.verification_requests_handled],
    ["Vehicles added",     metrics.vehicles_added],
  ];
  return (
    <div data-testid="screen-emp-performance" className="space-y-3">
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="text-lg font-display font-semibold text-slate-900">{name}</div>
        <div className="text-xs text-slate-500 font-mono">{login}</div>
        <div className="mt-3 grid grid-cols-2 gap-2">
          {entries.map(([l, v]) => (
            <div key={l} className="rounded-lg border border-slate-200 bg-slate-50 p-2.5">
              <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-slate-500">{l}</div>
              <div className="text-xl font-display font-semibold mt-0.5">{v ?? 0}</div>
            </div>
          ))}
        </div>
        <p className="mt-3 text-[11px] text-slate-500">Privacy-friendly aggregates only. No keystroke tracking.</p>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------
// SCREEN: Staff availability (mirrors StaffAvailability.jsx)
// -----------------------------------------------------------------
function StaffAvailabilityScreen({ data, onAction }) {
  const { online = [], offline = [], focusId, history = [], primary } = data;
  const iconMap = { available: PlayCircle, busy: PauseCircle, on_break: Coffee };
  return (
    <div data-testid="screen-staff-availability" className="space-y-3">
      <PageHeader icon={Users} title="Who's on the clock" subtitle="Inquiries route to a clocked-in staff with the right permissions. Owner is the fallback." />
      <div className="grid sm:grid-cols-3 gap-2">
        <StatCard label="Online now" value={online.length} tone="emerald" />
        <StatCard label="Offline"    value={offline.length} tone="slate" />
        <StatCard label="Team size"  value={online.length + offline.length} tone="sky" />
      </div>
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="font-display text-base font-semibold">Online <span className="text-slate-400 text-xs">({online.length})</span></div>
        <ul className="mt-2 divide-y divide-slate-100">
          {online.map((e) => (
            <li key={e.id} className={`py-2 flex items-center justify-between gap-2 ${e.id === focusId ? "bg-emerald-50 rounded-md px-2" : ""}`}
                data-testid={`staff-row-${e.id}`}>
              <div>
                <div className="text-sm font-medium">{e.name} <span className="text-xs text-slate-500 ml-1">{e.title}</span></div>
                <div className="text-[11px] text-slate-500 inline-flex items-center gap-1 mt-0.5">
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wider font-semibold bg-emerald-100 text-emerald-800">
                    {(() => { const M = iconMap[e.status] || PlayCircle; return <M className="w-3 h-3" />; })()}
                    {(e.status || "available").replace("_"," ")}
                  </span>
                  <span>Clocked in {e.clockedFor}</span>
                </div>
              </div>
              {e.id === focusId && primary && (
                <button onClick={onAction} data-testid={`staff-primary-${e.id}`}
                        className="text-xs px-2.5 py-1 rounded-lg border border-rose-200 text-rose-700 bg-white inline-flex items-center gap-1">
                  <LogOut className="w-3 h-3" /> {primary}
                </button>
              )}
            </li>
          ))}
        </ul>
      </div>
      {offline.length > 0 && (
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="font-display text-base font-semibold">Offline <span className="text-slate-400 text-xs">({offline.length})</span></div>
          <ul className="mt-2 divide-y divide-slate-100">
            {offline.map((e) => (
              <li key={e.id} className="py-2" data-testid={`staff-offline-${e.id}`}>
                <div className="text-sm font-medium">{e.name} <span className="text-xs text-slate-500 ml-1">{e.title}</span></div>
                <div className="text-[11px] text-slate-500 mt-0.5">Last seen {e.lastSeen}</div>
              </li>
            ))}
          </ul>
        </div>
      )}
      {history.length > 0 && (
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="font-display text-base font-semibold flex items-center gap-2"><Clock className="w-4 h-4 text-slate-500" /> Recent attendance</div>
          <table className="mt-2 w-full text-xs">
            <thead>
              <tr className="text-left border-b border-slate-200 text-[10px] uppercase tracking-wider text-slate-500">
                <th className="py-1.5 pr-2">Employee</th><th className="py-1.5 pr-2">In</th><th className="py-1.5 pr-2">Out</th><th className="py-1.5">Duration</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {history.map((s, i) => (
                <tr key={i} data-testid={`staff-history-row-${i}`}>
                  <td className="py-1.5 pr-2 font-medium">{s.name}</td>
                  <td className="py-1.5 pr-2 text-slate-700">{s.in}</td>
                  <td className="py-1.5 pr-2 text-slate-700">{s.out || <span className="text-emerald-700 font-medium">Open</span>}</td>
                  <td className="py-1.5 text-slate-700">{s.duration}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------
// SCREEN: Vehicles list (mirrors Vehicles.jsx)
// -----------------------------------------------------------------
function VehiclesListScreen({ data, onAction }) {
  const { vehicles = [], fleet, focusId, ctaLabel = "Add vehicle" } = data;
  return (
    <div data-testid="screen-vehicles-list" className="space-y-3">
      <PageHeader icon={Car} title="Vehicles" subtitle="Manage your fleet and their availability."
                   right={<button onClick={onAction} data-testid="vehicle-add-btn"
                                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#1B4332] text-white text-xs font-medium">
                     <Plus className="w-3.5 h-3.5" /> {ctaLabel}
                   </button>} />
      {fleet && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-900">
          Fleet capacity ({fleet.plan}): <b>{fleet.used} / {fleet.total} pts</b>
          <div className="mt-1.5 h-1.5 rounded-full bg-white/70 overflow-hidden">
            <div className="h-full bg-emerald-500" style={{ width: `${(fleet.used/Math.max(1,fleet.total))*100}%` }} />
          </div>
        </div>
      )}
      <div className="grid sm:grid-cols-2 gap-3">
        {vehicles.map((v) => (
          <div key={v.id} className="rounded-xl border border-slate-200 bg-white p-3" data-testid={`vehicle-card-${v.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="text-sm font-semibold text-slate-900">{v.name}</div>
                <div className="text-[11px] text-slate-500 font-mono">{v.plate}</div>
              </div>
              <StatusPill tone={pillToneForStatus(v.status)}>{v.status}</StatusPill>
            </div>
            <div className="mt-2 grid grid-cols-3 gap-2 text-[11px] text-slate-600">
              <div><div className="text-[9px] uppercase tracking-wider text-slate-400">Category</div><div className="text-slate-900">{v.category}</div></div>
              <div><div className="text-[9px] uppercase tracking-wider text-slate-400">Trans</div><div className="text-slate-900">{v.transmission}</div></div>
              <div><div className="text-[9px] uppercase tracking-wider text-slate-400">Seats</div><div className="text-slate-900">{v.seats}</div></div>
            </div>
            <div className="mt-2 flex items-center justify-between">
              <div className="text-sm font-mono">₱{v.price?.toLocaleString?.() ?? v.price}<span className="text-slate-500 text-xs"> /day</span></div>
              <div className="flex gap-2 text-xs">
                <button disabled className="text-slate-700 opacity-70">Damages</button>
                <button disabled={!!focusId && v.id !== focusId}
                        onClick={v.id === focusId ? onAction : undefined}
                        data-testid={`vehicle-view-${v.id}`}
                        className={`text-[#1B4332] font-medium ${focusId && v.id === focusId ? "" : "opacity-70"}`}>View</button>
                <button disabled className="text-slate-700 opacity-70">Edit</button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// -----------------------------------------------------------------
// SCREEN: Vehicle form (mirrors VehicleFormModal: basic tab)
// -----------------------------------------------------------------
const VEHICLE_TABS = ["Basic Info", "Technical", "Pricing", "Public Description", "Rules & Pickup", "Internal Notes", "Visibility"];
function VehicleFormScreen({ data, onAction }) {
  const [tab, setTab] = useState(0);
  const [name, setName] = useState(data.name || "");
  const [plate, setPlate] = useState(data.plate || "");
  return (
    <div data-testid="screen-vehicle-form" className="space-y-3">
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <div className="flex items-center justify-between p-3 border-b border-slate-200">
          <div className="text-sm font-semibold">Add vehicle</div>
          <X className="w-4 h-4 text-slate-400" />
        </div>
        <div className="px-3 pt-2 border-b border-slate-200 overflow-x-auto">
          <div className="flex gap-1 min-w-max">
            {VEHICLE_TABS.map((t, i) => (
              <button key={t} type="button" onClick={() => setTab(i)}
                      data-testid={`vf-tab-${i}`}
                      className={`px-3 py-1.5 text-xs rounded-t-md border-b-2 -mb-px whitespace-nowrap ${tab === i ? "border-[#2D6A4F] text-[#1B4332] font-semibold" : "border-transparent text-slate-500"}`}>
                {t}
              </button>
            ))}
          </div>
        </div>
        <div className="p-3 grid sm:grid-cols-2 gap-3 text-xs">
          {tab === 0 && <>
            <label className="sm:col-span-2">
              <span className="uppercase tracking-wider font-semibold text-slate-500">Name</span>
              <input value={name} onChange={(e)=>setName(e.target.value)} data-testid="vf-name"
                     className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 text-sm" />
            </label>
            <label>
              <span className="uppercase tracking-wider font-semibold text-slate-500">Make / brand</span>
              <input defaultValue={data.make || ""} className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 text-sm" />
            </label>
            <label>
              <span className="uppercase tracking-wider font-semibold text-slate-500">Model</span>
              <input defaultValue={data.model || ""} className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 text-sm" />
            </label>
            <label>
              <span className="uppercase tracking-wider font-semibold text-slate-500">Category</span>
              <select className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 bg-white text-sm">
                <option>{data.category || "Sedan"}</option>
              </select>
            </label>
            <label>
              <span className="uppercase tracking-wider font-semibold text-slate-500">Plate number</span>
              <input value={plate} onChange={(e)=>setPlate(e.target.value)} data-testid="vf-plate"
                     className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 text-sm" />
            </label>
          </>}
          {tab === 2 && <>
            <label className="sm:col-span-2">
              <span className="uppercase tracking-wider font-semibold text-slate-500">Price per day (PHP)</span>
              <input defaultValue={data.price || "1700"} data-testid="vf-price"
                     className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 text-sm" />
              <p className="text-[10px] text-emerald-700 mt-1">Using <b>{data.category || "Sedan"}</b> default (₱1,700/day).</p>
            </label>
            <label>
              <span className="uppercase tracking-wider font-semibold text-slate-500">Deposit</span>
              <input defaultValue={data.deposit || "1000"} className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 text-sm" />
            </label>
            <div className="sm:col-span-2 rounded-lg bg-slate-50 border border-slate-200 p-2 text-[11px] text-slate-700">
              This <b>{data.category || "Sedan"}</b> will use <b>3</b> fleet points.
            </div>
          </>}
          {tab !== 0 && tab !== 2 && (
            <div className="sm:col-span-2 rounded-md bg-slate-50 border border-slate-200 p-3 text-slate-500">
              (Other tab fields, not the focus of this scenario.)
            </div>
          )}
        </div>
      </div>
      <button onClick={onAction} data-testid="vf-submit"
              className="w-full rounded-lg bg-[#1B4332] text-white text-sm font-medium py-2.5">
        Save vehicle
      </button>
    </div>
  );
}

// -----------------------------------------------------------------
// SCREEN: Vehicle detail (compact)
// -----------------------------------------------------------------
function VehicleDetailScreen({ data, onAction }) {
  const { name, plate, status, next, primary } = data;
  return (
    <div data-testid="screen-vehicle-detail" className="space-y-3">
      <button className="text-xs text-slate-500 inline-flex items-center gap-1">
        <ChevronLeft className="w-3.5 h-3.5" /> Back to Vehicles
      </button>
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="flex items-start justify-between gap-2">
          <div>
            <h2 className="font-display text-lg font-semibold text-slate-900">{name}</h2>
            <div className="text-xs text-slate-500 font-mono">{plate}</div>
          </div>
          <StatusPill tone={pillToneForStatus(status)}>{status}</StatusPill>
        </div>
        {next && (
          <div className="mt-3 rounded-lg bg-slate-50 border border-slate-200 p-2 text-xs">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Next booking</div>
            <div className="text-slate-900 mt-0.5">{next}</div>
          </div>
        )}
      </div>
      {primary && (
        <button onClick={onAction} data-testid="vehicle-detail-primary"
                className="w-full rounded-lg bg-amber-600 text-white text-sm font-medium py-2.5">
          {primary}
        </button>
      )}
    </div>
  );
}

// -----------------------------------------------------------------
// SCREEN: Maintenance form (schedule window)
// -----------------------------------------------------------------
function MaintenanceFormScreen({ data, onAction }) {
  return (
    <div data-testid="screen-maintenance-form" className="space-y-3">
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="text-sm font-semibold text-slate-900 mb-2">Schedule maintenance</div>
        <div className="text-xs text-slate-600 mb-3">{data.vehicle} · {data.plate}</div>
        <div className="grid grid-cols-2 gap-3 text-xs">
          <label>
            <span className="uppercase tracking-wider font-semibold text-slate-500">Start</span>
            <input defaultValue={data.start} className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 text-sm" data-testid="maint-start" />
          </label>
          <label>
            <span className="uppercase tracking-wider font-semibold text-slate-500">End</span>
            <input defaultValue={data.end} className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 text-sm" data-testid="maint-end" />
          </label>
          <label className="col-span-2">
            <span className="uppercase tracking-wider font-semibold text-slate-500">Reason</span>
            <input defaultValue={data.reason} className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 text-sm" />
          </label>
        </div>
        <div className="mt-3 rounded-md bg-amber-50 border border-amber-200 text-amber-900 p-2 text-xs">
          Any incoming bookings that overlap will be blocked with a warning.
        </div>
      </div>
      <button onClick={onAction} data-testid="maint-save"
              className="w-full rounded-lg bg-[#1B4332] text-white text-sm font-medium py-2.5">
        Save maintenance window
      </button>
    </div>
  );
}

// -----------------------------------------------------------------
// SCREEN: Damage incident (mirrors IncidentNew.jsx shape)
// -----------------------------------------------------------------
function DamageIncidentScreen({ data, onAction }) {
  return (
    <div data-testid="screen-damage-incident" className="space-y-3">
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="text-sm font-semibold text-slate-900 mb-1">Log incident</div>
        <div className="text-xs text-slate-600 mb-3">{data.vehicle} · {data.plate}</div>
        {data.photo && (
          <div className="rounded-lg border border-rose-200 overflow-hidden mb-3">
            <div className="px-2 py-1 bg-rose-50 text-[10px] font-semibold text-rose-900 uppercase tracking-wider inline-flex items-center gap-1"><Camera className="w-3 h-3" /> Photo evidence</div>
            <img src={data.photo} alt="Damage" className="w-full aspect-[16/10] object-cover" />
          </div>
        )}
        <div className="grid grid-cols-2 gap-3 text-xs">
          <label>
            <span className="uppercase tracking-wider font-semibold text-slate-500">Title</span>
            <input defaultValue={data.title} className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 text-sm" data-testid="incident-title" />
          </label>
          <label>
            <span className="uppercase tracking-wider font-semibold text-slate-500">Severity</span>
            <select defaultValue={data.severity} className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 bg-white text-sm">
              <option>Minor</option><option>Moderate</option><option>Major</option>
            </select>
          </label>
          <label className="col-span-2">
            <span className="uppercase tracking-wider font-semibold text-slate-500">Notes</span>
            <textarea defaultValue={data.notes} rows={3} className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 text-sm" data-testid="incident-notes" />
          </label>
        </div>
        {data.affected?.length > 0 && (
          <div className="mt-3 rounded-md bg-amber-50 border border-amber-200 p-2 text-xs">
            <div className="font-semibold text-amber-900">Future bookings affected</div>
            <ul className="mt-1 text-slate-800">
              {data.affected.map((a, i) => <li key={i}>· {a.customer}: {a.when}</li>)}
            </ul>
          </div>
        )}
      </div>
      <button onClick={onAction} data-testid="incident-save"
              className="w-full rounded-lg bg-[#1B4332] text-white text-sm font-medium py-2.5">
        Log incident & surface affected bookings
      </button>
    </div>
  );
}

// -----------------------------------------------------------------
// SCREEN: Daily checklists today (mirrors DailyChecklists.jsx)
// -----------------------------------------------------------------
function ChecklistsTodayScreen({ data, onAction }) {
  const { groups = [], focus } = data;
  return (
    <div data-testid="screen-checklists-today" className="space-y-3">
      <PageHeader icon={ClipboardCheck} title="Daily checklists" subtitle="Opening, closing, vehicle readiness, cleaning, handover." />
      <div className="flex gap-2 border-b border-slate-200 pb-1">
        <div className="text-xs px-3 py-1.5 border-b-2 border-emerald-700 text-emerald-800 font-semibold">Today</div>
        <div className="text-xs px-3 py-1.5 text-slate-500">History</div>
        <div className="text-xs px-3 py-1.5 text-slate-500">Templates</div>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        {groups.map((g) => (
          <div key={g.type} className={`rounded-2xl border p-3 bg-white ${g.type === focus ? "border-emerald-300 ring-2 ring-emerald-200" : "border-slate-200"}`}
               data-testid={`checklist-today-${g.type}`}>
            <div className="text-[11px] text-slate-500">{g.typeLabel}</div>
            <div className="text-sm font-semibold text-slate-900">{g.name}</div>
            <div className="text-[11px] text-slate-500 mt-0.5">{g.itemCount} items</div>
            <div className="mt-2 flex items-center justify-between">
              {g.type === focus ? (
                <button onClick={onAction} data-testid={`checklist-start-${g.type}`}
                        className="px-3 py-1.5 rounded-lg bg-emerald-700 text-white text-xs font-semibold inline-flex items-center gap-1">
                  <Plus className="w-3 h-3" /> New
                </button>
              ) : (
                <button disabled className="px-3 py-1.5 rounded-lg bg-emerald-700 text-white text-xs opacity-60 inline-flex items-center gap-1">
                  <Plus className="w-3 h-3" /> New
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// -----------------------------------------------------------------
// SCREEN: Checklist run (mirrors RunEditor)
// -----------------------------------------------------------------
function ChecklistRunScreen({ data, onAction }) {
  const [items, setItems] = useState(data.items || []);
  const toggle = (i) => setItems(items.map((it, idx) => idx === i ? { ...it, checked: !it.checked } : it));
  const done = items.every(it => it.checked || !it.required);
  return (
    <div data-testid="screen-checklist-run" className="space-y-3">
      <button className="text-xs text-slate-500 inline-flex items-center gap-1">
        <ChevronLeft className="w-3.5 h-3.5" /> Back to checklists
      </button>
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="font-display text-base font-semibold text-slate-900 inline-flex items-center gap-2">
          <ClipboardCheck className="w-4 h-4 text-emerald-700" /> {data.name}
        </div>
        <div className="text-xs text-slate-600 mt-1">{data.type} · {data.vehicle}</div>
      </div>
      <ul className="space-y-2">
        {items.map((it, i) => (
          <li key={i} className={`rounded-xl border bg-white p-2.5 ${it.checked ? "border-emerald-200" : "border-slate-200"}`}
              data-testid={`checklist-item-${i}`}>
            <label className="flex items-start gap-2.5 cursor-pointer text-sm">
              <input type="checkbox" checked={!!it.checked} onChange={() => toggle(i)} className="mt-1"
                     data-testid={`checklist-item-check-${i}`} />
              <div className="flex-1">
                <div className="font-medium">{it.label}{it.required && <span className="ml-1 text-rose-600">*</span>}</div>
                {it.note && <div className="text-[11px] text-slate-500 italic mt-0.5">Note: {it.note}</div>}
              </div>
            </label>
          </li>
        ))}
      </ul>
      <button onClick={onAction} disabled={!done}
              data-testid="checklist-submit-btn"
              className="w-full rounded-lg bg-emerald-700 text-white text-sm font-semibold py-2.5 disabled:opacity-50 inline-flex items-center justify-center gap-2">
        <CheckCircle2 className="w-4 h-4" /> {done ? "Submit checklist" : "Tick required items to submit"}
      </button>
    </div>
  );
}

// -----------------------------------------------------------------
// SCREEN: Review invite (mirrors ReviewInvite send)
// -----------------------------------------------------------------
function ReviewInviteScreen({ data, onAction }) {
  return (
    <div data-testid="screen-review-invite" className="space-y-3">
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="text-sm font-semibold text-slate-900">Send review invite</div>
        <div className="text-xs text-slate-600 mt-1">
          Customer <b>{data.customer}</b> completed booking on <b>{data.vehicle}</b>.
        </div>
        <div className="mt-3 rounded-lg bg-slate-50 border border-slate-200 p-2 text-xs">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Channel</div>
          <div>{data.channel}</div>
        </div>
        <div className="mt-2 rounded-lg bg-slate-50 border border-slate-200 p-2 text-xs">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Link</div>
          <div className="font-mono text-slate-800">confirmph.app/r/DEMO-REVIEW-9F2A</div>
        </div>
        <div className="text-[11px] text-slate-500 mt-2">
          Only customers with a completed ConfirmPH booking can leave a review: that&apos;s what makes them &ldquo;verified&rdquo;.
        </div>
      </div>
      <button onClick={onAction} data-testid="review-invite-send"
              className="w-full rounded-lg bg-[#1B4332] text-white text-sm font-medium py-2.5">
        <Send className="w-4 h-4 inline mr-1" /> Send invite
      </button>
    </div>
  );
}

// -----------------------------------------------------------------
// SCREEN: Public review appears
// -----------------------------------------------------------------
function PublicReviewScreen({ data }) {
  return (
    <div data-testid="screen-public-review" className="space-y-3">
      <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-900">
        <div className="font-semibold">Booking-linked review published on your public profile</div>
        <div className="mt-0.5">Guests searching your business will now see this review.</div>
      </div>
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="flex items-center gap-2">
          <User className="w-4 h-4 text-slate-500" />
          <div className="text-sm font-semibold">{data.author}</div>
          <StatusPill tone="emerald">Booking-linked</StatusPill>
        </div>
        <div className="mt-1 flex items-center gap-0.5 text-amber-500">
          {[...Array(5)].map((_, i) => <Star key={i} className={`w-4 h-4 ${i < (data.stars || 5) ? "fill-amber-500" : "stroke-amber-300"}`} />)}
        </div>
        <p className="mt-2 text-sm text-slate-800">&ldquo;{data.body}&rdquo;</p>
        <div className="text-[11px] text-slate-500 mt-2">{data.when} · {data.vehicle}</div>
      </div>
      <div className="text-[11px] text-slate-500 italic flex items-center gap-1">
        <ExternalLink className="w-3 h-3" /> Same review shows on the ConfirmPH Business Page and full Website.
      </div>
    </div>
  );
}

// -----------------------------------------------------------------
// SCREEN: Confirmation preview (compact, non-primary)
// -----------------------------------------------------------------
function ConfirmationPreviewScreen({ data, onAction }) {
  return (
    <div data-testid="screen-confirmation-preview" className="space-y-3">
      <div className="rounded-xl border border-emerald-300 bg-white p-4">
        <div className="text-[10px] uppercase tracking-wider text-emerald-800 font-semibold">Fresh confirmation link generated</div>
        <div className="mt-2 rounded-lg bg-slate-50 border border-slate-200 p-2 text-xs font-mono">confirmph.app/c/DEMO-{data.id || "8F2A"}</div>
        <div className="mt-2 text-xs text-slate-700">
          <b>{data.customer}</b> · {data.vehicle}
          <div className="mt-0.5">{data.pickup} → {data.ret}</div>
        </div>
      </div>
      <button onClick={onAction} data-testid="confirmation-preview-copy"
              className="w-full rounded-lg bg-[#1B4332] text-white text-sm font-medium py-2.5">
        Copy & send to customer
      </button>
    </div>
  );
}

// -----------------------------------------------------------------
// SCREEN: Banner info (used as generic "outcome" screen)
// -----------------------------------------------------------------
function BannerInfoScreen({ data, onAction }) {
  const tone = data.tone === "warn" ? "border-amber-300 bg-amber-50 text-amber-900" :
                data.tone === "danger" ? "border-rose-300 bg-rose-50 text-rose-900" :
                                          "border-emerald-300 bg-emerald-50 text-emerald-900";
  return (
    <div className="space-y-3">
      <div className={`rounded-xl border p-4 ${tone}`} data-testid="screen-banner-info">
        <div className="flex items-start gap-2">
          {data.tone === "warn" || data.tone === "danger" ? <AlertTriangle className="w-5 h-5 mt-0.5 flex-shrink-0" /> : <CheckCircle2 className="w-5 h-5 mt-0.5 flex-shrink-0" />}
          <div>
            <div className="font-semibold text-sm">{data.title}</div>
            {data.body && <div className="text-xs mt-1">{data.body}</div>}
          </div>
        </div>
      </div>
      {data.primary && (
        <button onClick={onAction} data-testid="banner-info-primary"
                className="w-full rounded-lg bg-[#1B4332] text-white text-sm font-medium py-2.5">
          {data.primary}
        </button>
      )}
    </div>
  );
}

// Fake unused import guard
useMemo; Circle;
